/**
 * 账户API服务
 * 封装所有账户相关的API调用
 */

import { apiClient } from './ApiClient';
import { logger } from '../utils/logger';

export interface AccountInfo {
  userId: string;
  balance: number;
  availableBalance: number;
  frozenMargin: number;
  totalDeposit: number;
  totalWithdrawal: number;
  totalPnl: number;
  totalFee: number;
  totalTrades: number;
  winningTrades: number;
  createdAt: string;
  updatedAt: string;
}

export interface BalanceInfo {
  balance: number;
  availableBalance: number;
  frozenMargin: number;
  unrealizedPnl?: number;
}

export interface DepositRequest {
  amount: number;
  method?: string;
  notes?: string;
}

export interface WithdrawalRequest {
  amount: number;
  method?: string;
  address?: string;
  notes?: string;
}

export interface DepositRecord {
  id: string;
  amount: number;
  fee: number;
  netAmount: number;
  method: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  notes?: string;
  rebateAmount?: number;
}

export interface WithdrawalRecord {
  id: string;
  amount: number;
  fee: number;
  netAmount: number;
  method: string;
  address?: string;
  status: string;
  createdAt: string;
  completedAt?: string;
  notes?: string;
}

export interface TransactionRecord {
  id: string;
  type: 'deposit' | 'withdrawal' | 'trade' | 'fee';
  amount: number;
  balance: number;
  status: string;
  createdAt: string;
  notes?: string;
}

export class AccountApiService {
  /**
   * 获取账户信息
   */
  async getAccount(): Promise<AccountInfo | null> {
    try {
      const response = await apiClient.get<{ account: AccountInfo }>('/api/accounts/me');

      if (response.error || !response.data) {
        logger.error('获取账户信息失败:', response.error);
        return null;
      }

      return response.data.account;
    } catch (error: any) {
      logger.error('获取账户信息异常:', error);
      return null;
    }
  }

  /**
   * 获取余额
   */
  async getBalance(): Promise<BalanceInfo | null> {
    try {
      const response = await apiClient.get<BalanceInfo>('/api/accounts/balance');

      if (response.error || !response.data) {
        logger.error('获取余额失败:', response.error);
        return null;
      }

      return response.data;
    } catch (error: any) {
      logger.error('获取余额异常:', error);
      return null;
    }
  }

  /**
   * 获取充值记录
   */
  async getDeposits(limit: number = 50): Promise<DepositRecord[]> {
    try {
      const response = await apiClient.get<{ deposits: DepositRecord[] }>(
        `/api/accounts/deposits?limit=${limit}`
      );

      if (response.error || !response.data) {
        logger.error('获取充值记录失败:', response.error);
        return [];
      }

      return response.data.deposits;
    } catch (error: any) {
      logger.error('获取充值记录异常:', error);
      return [];
    }
  }

  /**
   * 获取提现记录
   */
  async getWithdrawals(limit: number = 50): Promise<WithdrawalRecord[]> {
    try {
      const response = await apiClient.get<{ withdrawals: WithdrawalRecord[] }>(
        `/api/accounts/withdrawals?limit=${limit}`
      );

      if (response.error || !response.data) {
        logger.error('获取提现记录失败:', response.error);
        return [];
      }

      return response.data.withdrawals;
    } catch (error: any) {
      logger.error('获取提现记录异常:', error);
      return [];
    }
  }

  /**
   * 获取交易记录（综合）
   */
  async getTransactions(limit: number = 100): Promise<TransactionRecord[]> {
    try {
      const response = await apiClient.get<{ transactions: TransactionRecord[] }>(
        `/api/accounts/transactions?limit=${limit}`
      );

      if (response.error || !response.data) {
        logger.error('获取交易记录失败:', response.error);
        return [];
      }

      return response.data.transactions;
    } catch (error: any) {
      logger.error('获取交易记录异常:', error);
      return [];
    }
  }
}

// 导出单例
export const accountApi = new AccountApiService();

